import React, { useState } from "react";
import { Flame, Sun, CloudRain, CloudSnow, Waves } from "lucide-react";
import ModuleNavigation from "../components/ModuleNavigation";

const caseStudies = [
  {
    id: "wildfires",
    title: "Wildfires",
    icon: Flame,
    color: "#ef4444",
    image: "https://placehold.co/800x400/359381/FFFFFF/png?text=Wildfires",
    content: {
      story:
        "In 2023, Canada experienced its worst wildfire season on record, burning over 45.7 million acres (18.5 million hectares) of land—more than double the previous record. Smoke from these fires affected air quality for millions across North America, with cities as far as New York experiencing hazardous air quality levels.",
      why2024:
        "Climate change has created hotter, drier conditions in northern forests, extending fire seasons and increasing the intensity of fires. Areas previously considered low-risk are now experiencing unprecedented fire activity.",
      emissions: [
        "Longer drought periods increase forest flammability",
        "Higher temperatures create more lightning strikes that start fires",
        "Carbon feedback loop: fires release carbon, worsening climate change",
      ],
    },
  },
  {
    id: "heat",
    title: "Extreme Heat",
    icon: Sun,
    color: "#f59e0b",
    image: "https://placehold.co/800x400/359381/FFFFFF/png?text=Extreme+Heat",
    content: {
      story:
        "The summer of 2023 witnessed record-breaking heat waves across Southern Europe, North Africa, and the Middle East. In places like Sicily and Algeria, temperatures surpassed 48°C (118°F). This extreme heat led to thousands of heat-related deaths, widespread wildfires, and major disruptions to tourism, agriculture, and infrastructure.",
      why2024:
        "Climate models consistently show that heat waves are becoming more frequent, intense, and longer-lasting due to greenhouse gas emissions. What were once considered rare events are now occurring regularly, with 2024 likely to set new temperature records.",
      emissions: [
        "Urban heat islands amplify temperatures in cities",
        "CO₂ and methane trap heat in the atmosphere",
        "Reduced snow cover decreases earth's reflectivity",
      ],
    },
  },
  {
    id: "air",
    title: "Air Quality",
    icon: CloudRain,
    color: "#6b7280",
    image: "https://placehold.co/800x400/359381/FFFFFF/png?text=Air+Quality",
    content: {
      story:
        "Delhi, India regularly experiences severe air pollution events, with particulate matter (PM2.5) levels often exceeding 20 times the WHO safe limit. In November 2023, schools were forced to close and outdoor activities banned as the Air Quality Index surpassed 800 in some areas (anything above 300 is considered hazardous).",
      why2024:
        "While industrial emissions and agricultural burning are significant factors, climate change exacerbates these issues through more stagnant air conditions, reduced rainfall to clear pollutants, and increased wildfire smoke.",
      emissions: [
        "Fossil fuel combustion produces particulate matter",
        "Higher temperatures accelerate formation of ground-level ozone",
        "Increased pollen seasons due to climate change worsen respiratory conditions",
      ],
    },
  },
  {
    id: "glacier",
    title: "Glacier Floods",
    icon: CloudSnow,
    color: "#3b82f6",
    image: "https://placehold.co/800x400/359381/FFFFFF/png?text=Glacier+Floods",
    content: {
      story:
        "In July 2023, a glacial lake outburst flood in the Himalayas devastated communities in northern India. As warming temperatures accelerated glacier melt, a natural dam failed, releasing millions of cubic meters of water that destroyed bridges, roads, and homes downstream.",
      why2024:
        "As global temperatures rise, glaciers worldwide are retreating at unprecedented rates. This creates unstable lakes behind natural moraine dams that can fail catastrophically. The IPCC has identified over 15,000 glacial lakes with potential outburst risk.",
      emissions: [
        "Every 1°C of warming accelerates glacier melt by 25-30%",
        "Black carbon deposits from fossil fuel combustion darken glacier surfaces",
        "Thermal expansion of water bodies increases pressure on natural dams",
      ],
    },
  },
  {
    id: "sea",
    title: "Sea-level Rise",
    icon: Waves,
    color: "#0ea5e9",
    image: "https://placehold.co/800x400/359381/FFFFFF/png?text=Sea-level+Rise",
    content: {
      story:
        "Tuvalu, a Pacific island nation with a maximum elevation of just 4.5 meters (15 ft), is facing an existential crisis from rising sea levels. Already, 40% of its capital area is underwater at high tide, and saltwater intrusion is making agriculture increasingly difficult.",
      why2024:
        "Sea levels rose 4-5mm in 2023 alone, accelerating from the 20th-century average of 1.4mm per year. Thermal expansion of warming oceans and melting ice sheets in Greenland and Antarctica are driving this acceleration, with potential multi-meter rises projected by 2100.",
      emissions: [
        "CO₂ emissions cause both warming (thermal expansion) and ice melt",
        "Methane from natural gas and agriculture intensifies the warming effect",
        "Loss of ice decreases earth's reflectivity, creating a warming feedback loop",
      ],
    },
  },
];

const CaseStudies = () => {
  const [activeTab, setActiveTab] = useState("wildfires");
  const activeCase = caseStudies.find((study) => study.id === activeTab);
  const [isCompleted, setIsCompleted] = useState(false);

  const handleMarkComplete = () => {
    setIsCompleted(!isCompleted);
    // In a real app, you would save this to localStorage or a backend
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-10">
        <h1 className="text-4xl font-bold text-primary-600 mb-2">
          Case Studies<span className="text-edu-orange">:</span> Climate in
          Action
        </h1>
        <div className="w-24 h-1 bg-edu-orange mx-auto mt-2 mb-6"></div>
      </div>

      <div className="bg-primary-100 p-6 rounded-lg mb-12">
        <p className="text-lg text-gray-800">
          These real-world case studies demonstrate the complex relationship
          between human activities, carbon emissions, and climate disasters.
          Explore how different communities are experiencing and responding to
          climate change.
        </p>
      </div>

      {/* Case Study Tabs */}
      <div className="flex flex-wrap border-b border-gray-200 mb-8 overflow-x-auto">
        {caseStudies.map((study) => (
          <button
            key={study.id}
            className={`px-4 py-2 mr-2 flex items-center whitespace-nowrap ${
              activeTab === study.id
                ? "text-white bg-primary-500 rounded-t-lg font-medium"
                : "text-gray-600 hover:text-primary-500"
            }`}
            onClick={() => setActiveTab(study.id)}
          >
            <study.icon
              className="w-5 h-5 mr-2"
              style={{ color: activeTab === study.id ? "white" : study.color }}
            />
            {study.title}
          </button>
        ))}
      </div>

      {/* Case Study Content */}
      {activeCase && (
        <div className="mb-16">
          <div className="rounded-lg overflow-hidden mb-8">
            <img
              src={activeCase.image}
              alt={activeCase.title}
              className="w-full h-80 object-cover"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                {activeCase.title}
              </h2>
              <p className="text-gray-600 mb-8">{activeCase.content.story}</p>

              <h3 className="text-xl font-medium text-gray-800 mb-3">
                Link to Emissions
              </h3>
              <ul className="list-disc pl-5 mb-8">
                {activeCase.content.emissions.map((item, index) => (
                  <li key={index} className="text-gray-600 mb-2">
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-medium text-primary-500 mb-4 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5 mr-2"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4M12 8h.01" />
                </svg>
                Why it was worse in 2024
              </h3>
              <p className="text-gray-700">{activeCase.content.why2024}</p>
            </div>
          </div>
        </div>
      )}

      {/* Module Navigation */}
      <ModuleNavigation
        prevLink="/climate-science"
        prevText="Previous: Climate Science"
        nextLink="/carbon-footprint"
        nextText="Next: Carbon Footprint"
        onMarkComplete={handleMarkComplete}
        isCompleted={isCompleted}
      />
    </div>
  );
};

export default CaseStudies;
